import { DocumentUploadPrompt, PromptType } from 'jobs/prompts/prompt-types'
import { action, computed, observable, makeObservable } from 'mobx'
import { IPromptInputEditorViewModel } from './../IPromptInputEditorViewModel'
import DocumentUploadItemViewModel from './DocumentUploadItemViewModel'

/**
 * View Model for the Document Upload Editor
 */
export default class DocumentUploadInputEditorViewModel
  implements IPromptInputEditorViewModel
{
  /**
   * Minimum allowable number of document upload requests.
   */
  private readonly MINIMUM = 1

  /**
   * Maximum allowable number of document upload requests.
   */
  private readonly MAXIMUM = 10

  /**
   * List of Document Upload Item View Models in use.
   */
  @observable
  documentUploadItems = [new DocumentUploadItemViewModel()]

  constructor() {
    makeObservable(this)
  }

  /**
   * Adds a new document upload line item.
   */
  @action.bound
  addDocumentUploadItem() {
    if (!this.isAppendable) {
      return
    }

    this.documentUploadItems.push(new DocumentUploadItemViewModel())
  }

  /**
   * Removes the document upload line item with the given ID from the
   * list of documents to upload.
   *
   * @param id
   * The ID of the document upload line item to remove.
   */
  @action.bound
  removeDocumentUploadItem(id: string) {
    if (!this.isRemovable) {
      return
    }

    this.documentUploadItems = this.documentUploadItems.filter(
      (vm) => vm.id !== id
    )
  }

  /**
   * Validates all Document Upload Items in use, and returns the boolean
   * validation result.
   */
  @action.bound
  validate() {
    return this.documentUploadItems.map((vm) => vm.validate()).every(Boolean)
  }

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    this.documentUploadItems = [new DocumentUploadItemViewModel()]
  }

  /**
   * Maps this View Model to the Document Upload Prompt Input Type.
   */
  toInput(): DocumentUploadPrompt {
    return {
      type: PromptType.DocumentUpload,
      options: this.documentUploadItems.map((item) => ({
        id: item.id,
        title: item.title,
      })),
    }
  }

  /**
   * Indicates whether the displayed document upload items are removable.
   */
  @computed
  get isRemovable() {
    return this.documentUploadItems.length > this.MINIMUM
  }

  /**
   * Indicates whether new document upload items can be added.
   */
  @computed
  get isAppendable() {
    return this.documentUploadItems.length < this.MAXIMUM
  }
}
